import React, { useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash } from 'react-icons/fi';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPosts = async () => {
    try {
      const res = await api.get('/api/blog');
      setPosts(res.data);
    } catch (err) {
      console.error('Error al obtener posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este post?')) return;
    try {
      await api.delete(`/api/blog/${id}`);
      fetchPosts();
    } catch (err) {
      console.error('Error al eliminar:', err);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen font-[Manrope]">
      <div className="mx-auto max-w-7xl">
        <div className="bg-white shadow-md rounded-xl p-4 sm:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-neutral-800">Blog</h1>
              <p className="mt-1 text-xs sm:text-sm text-neutral-500">
                Lista de publicaciones creadas en el sistema
              </p>
            </div>
            <button
              onClick={() => navigate('/blog/create')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-black text-white hover:bg-indigo-700 text-xs sm:text-sm"
            >
              <FiPlus className="text-base" />
              Nuevo Post
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <p className="text-neutral-500 text-center py-8 text-xs sm:text-sm">Cargando...</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xs sm:text-sm text-neutral-400">No hay publicaciones aún</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto rounded-lg shadow-sm bg-white">
                <table className="min-w-full text-xs sm:text-sm text-left">
                  <thead className="bg-gray-50 border-b text-neutral-700">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Título</th>
                      <th className="px-4 py-3">Slug</th>
                      <th className="px-4 py-3">Publicado</th>
                      <th className="px-4 py-3">Fecha</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post) => (
                      <tr key={post.id} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-neutral-800">{post.title}</td>
                        <td className="px-4 py-3 text-neutral-500">{post.slug}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              post.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {post.is_published ? 'Sí' : 'No'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-neutral-500">
                          {post.published_at
                            ? format(new Date(post.published_at), 'PPP', { locale: es })
                            : '-'}
                        </td>
                        <td className="px-4 py-3 flex justify-end gap-2">
                          <button
                            onClick={() => navigate(`/blog/edit/${post.id}`)}
                            className="p-2 rounded-md hover:bg-gray-100"
                          >
                            <FiEdit2 className="text-neutral-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 rounded-md hover:bg-gray-100"
                          >
                            <FiTrash className="text-red-500" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-neutral-800 text-sm">{post.title}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/blog/edit/${post.id}`)}
                            className="p-2 rounded-md hover:bg-gray-100"
                          >
                            <FiEdit2 className="text-neutral-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 rounded-md hover:bg-gray-100"
                          >
                            <FiTrash className="text-red-500" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-neutral-600">Slug: </span>
                        <span className="text-xs text-neutral-500">{post.slug}</span>
                      </div>
                      <div>
                        <span className="text-xs text-neutral-600">Publicado: </span>
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            post.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {post.is_published ? 'Sí' : 'No'}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-neutral-600">Fecha: </span>
                        <span className="text-xs text-neutral-500">
                          {post.published_at
                            ? format(new Date(post.published_at), 'PPP', { locale: es })
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogList;